import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl, type IntlShape } from "react-intl";
import { AdminMasterDetail } from "@/components/admin/AdminMasterDetail";
import { Button } from "@/components/design/Button";
import { MenuItem } from "@/components/design/MenuItem";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { TextArea, TextInput } from "@/components/design/TextField";
import {
  CommunicationLanguage,
  EmailTemplateKind,
  EmailTemplateSource,
  EmailTemplateStorageStatus,
  EmailTemplatesDocument,
  type EmailTemplatesQuery,
  UpdateEmailTemplateDocument,
} from "@/graphql/generated/operations";
import { COMMUNICATION_LANGUAGE_OPTIONS } from "@/lib/communication-language";
import { LocalizedError } from "@/lib/localized-error";
import { usePendingScope } from "@/lib/pending-edits";
import { userErrorMessage } from "@/lib/user-error";
import { EmailTemplateLanguagePreview } from "./EmailTemplateLanguagePreview";
import { InstanceMailLanguageCard } from "./InstanceMailLanguageCard";
import { LegacyEmailTemplateNotice } from "./LegacyEmailTemplateNotice";
import { SettingsCard } from "./SettingsCard";

interface Draft {
  subject: string | null;
  body: string | null;
}

// Validation rules travel with the template: placeholders and length limits come from Genesis, so a limit
// changed there never leaves a stale copy here. Genesis stays the normative validator.
interface ValidatedTemplate extends Draft {
  defaultSubject: string;
  defaultBody: string;
  placeholders: ReadonlyArray<{ token: string; required: boolean }>;
  maxSubjectLength: number;
  maxBodyLength: number;
}

type TemplateView = "editor" | "preview";

type PreviewBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "divider" };

const LINK_TOKEN = "{link}";
const APP_NAME_TOKEN = "{appName}";
const LINK_SENTINEL = "\uE000EMAIL_LINK\uE001";
const TOKEN_PATTERN = /\{[A-Za-z][A-Za-z0-9]*}/g;

function renderPreviewValue(value: string, appName: string): string {
  return value.replaceAll(APP_NAME_TOKEN, () => appName);
}

function previewBlocks(body: string, appName: string): PreviewBlock[] {
  const rendered = body
    .replaceAll(LINK_TOKEN, LINK_SENTINEL)
    .replaceAll(APP_NAME_TOKEN, () => appName)
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n");
  const blocks: PreviewBlock[] = [];
  let paragraph: string[] = [];
  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push({ kind: "paragraph", text: paragraph.join("\n") });
    paragraph = [];
  };

  for (const line of rendered.split("\n")) {
    if (line.trim() === "---") {
      flushParagraph();
      blocks.push({ kind: "divider" });
    } else if (line.trim() === "") {
      flushParagraph();
    } else {
      paragraph.push(line);
    }
  }
  flushParagraph();
  return blocks;
}

function PreviewParagraph({ text, linkLabel }: { text: string; linkLabel: string }) {
  const parts = text.split(LINK_SENTINEL);
  return (
    <p className="whitespace-pre-line text-smycles-meta leading-6 text-ink">
      {parts.map((part, index) => (
        <span key={`${index}-${part}`}>
          {index > 0 ? (
            <span className="font-medium text-ink underline underline-offset-2">{linkLabel}</span>
          ) : null}
          {part}
        </span>
      ))}
    </p>
  );
}

function EmailTemplatePreview({
  subject,
  body,
  appName,
  subjectLabel,
  previewLabel,
  approximationHint,
  linkLabel,
  emptyBodyLabel,
  emptySubjectLabel,
}: {
  subject: string;
  body: string;
  appName: string;
  subjectLabel: string;
  previewLabel: string;
  approximationHint: string;
  linkLabel: string;
  emptyBodyLabel: string;
  emptySubjectLabel: string;
}) {
  const blocks = previewBlocks(body, appName);
  return (
    <div className="box-flat overflow-hidden bg-page" role="region" aria-label={previewLabel}>
      <div className="border-b border-ink/10 px-5 py-4">
        <div className="smycles-field-label">{subjectLabel}</div>
        <div className="mt-1 text-smycles-body font-medium text-ink">
          {renderPreviewValue(subject, appName) || emptySubjectLabel}
        </div>
      </div>
      <div className="border-b border-ink/10 px-5 py-3">
        <p className="smycles-field-meta text-ink-muted">{approximationHint}</p>
      </div>
      <div className="mx-auto max-w-[42rem] space-y-4 px-5 py-6">
        {blocks.length === 0 ? (
          <div className="smycles-field-meta text-ink-muted">{emptyBodyLabel}</div>
        ) : (
          blocks.map((block, index) =>
            block.kind === "divider" ? (
              <hr key={`divider-${index}`} className="border-ink/10" />
            ) : (
              <PreviewParagraph key={`paragraph-${index}`} text={block.text} linkLabel={linkLabel} />
            ),
          )
        )}
      </div>
    </div>
  );
}

function validationMessage(
  intl: IntlShape,
  template: ValidatedTemplate,
  subject: string | null,
  body: string | null,
  appName: string,
): string | null {
  const effectiveSubject = subject ?? template.defaultSubject;
  const effectiveBody = body ?? template.defaultBody;

  if (appName.includes("\r") || appName.includes("\n")) {
    return intl.formatMessage({
      id: "adminSettings.emailTemplates.validation.applicationNameSingleLine",
      defaultMessage: "The app name must contain exactly one line. Change it in system settings.",
    });
  }
  if (effectiveSubject.includes(LINK_TOKEN)) {
    return intl.formatMessage(
      {
        id: "adminSettings.emailTemplates.validation.linkInSubject",
        defaultMessage: "The {token} placeholder is not allowed in the subject.",
      },
      { token: LINK_TOKEN },
    );
  }
  for (const placeholder of template.placeholders) {
    if (placeholder.required && !effectiveBody.includes(placeholder.token)) {
      return intl.formatMessage(
        {
          id: "adminSettings.emailTemplates.validation.requiredPlaceholder",
          defaultMessage: "Content must contain the required {token} placeholder.",
        },
        { token: placeholder.token },
      );
    }
  }
  if (
    effectiveSubject.includes(LINK_SENTINEL) ||
    effectiveBody.includes(LINK_SENTINEL) ||
    appName.includes(LINK_SENTINEL)
  ) {
    return intl.formatMessage({
      id: "adminSettings.emailTemplates.validation.reservedSequence",
      defaultMessage: "The template contains an unsupported control sequence.",
    });
  }

  const allowedTokens = new Set(template.placeholders.map((placeholder) => placeholder.token));
  for (const value of [effectiveSubject, effectiveBody]) {
    for (const token of value.match(TOKEN_PATTERN) ?? []) {
      if (!allowedTokens.has(token)) {
        return intl.formatMessage(
          {
            id: "adminSettings.emailTemplates.validation.unknownPlaceholder",
            defaultMessage: "The placeholder {token} is not supported.",
          },
          { token },
        );
      }
    }
  }

  const renderedSubject = effectiveSubject.replaceAll(APP_NAME_TOKEN, () => appName);
  const renderedBody = effectiveBody.replaceAll(APP_NAME_TOKEN, () => appName);
  if (renderedSubject.includes("\r") || renderedSubject.includes("\n")) {
    return intl.formatMessage({
      id: "adminSettings.emailTemplates.validation.subjectSingleLine",
      defaultMessage: "The subject must contain exactly one line.",
    });
  }
  if (renderedSubject.length > template.maxSubjectLength) {
    return intl.formatMessage(
      {
        id: "adminSettings.emailTemplates.validation.subjectTooLong",
        defaultMessage: "The subject must not exceed {max} characters after inserting the app name.",
      },
      { max: template.maxSubjectLength },
    );
  }
  if (renderedBody.length > template.maxBodyLength) {
    return intl.formatMessage(
      {
        id: "adminSettings.emailTemplates.validation.bodyTooLong",
        defaultMessage: "Content must not exceed {max} characters after inserting the app name.",
      },
      { max: template.maxBodyLength },
    );
  }
  return null;
}

type Template = EmailTemplatesQuery["emailTemplates"][number];

// One draft per variant: the invitation has one text, the mails genesis sends itself one per language
// (@external-adr GENESIS-0097-a-mail-is-written-in-the-language-resolved-for-its-recipient). Switching the language
// keeps the other language's draft, and the kind's one pending scope saves both.
function draftKey(template: Pick<Template, "kind" | "language">): string {
  return `${template.kind}:${template.language ?? "ALL"}`;
}

function uniqueKinds(templates: readonly Template[]): EmailTemplateKind[] {
  return templates.reduce<EmailTemplateKind[]>(
    (kinds, template) => (kinds.includes(template.kind) ? kinds : [...kinds, template.kind]),
    [],
  );
}

function sourceLabel(intl: IntlShape, source: EmailTemplateSource): string {
  switch (source) {
    case EmailTemplateSource.Custom:
      return intl.formatMessage({ id: "adminSettings.emailTemplates.source.CUSTOM", defaultMessage: "Own text" });
    case EmailTemplateSource.Legacy:
      return intl.formatMessage({
        id: "adminSettings.emailTemplates.source.LEGACY",
        defaultMessage: "Previous bilingual text",
      });
    default:
      return intl.formatMessage({ id: "adminSettings.emailTemplates.source.DEFAULT", defaultMessage: "Built-in text" });
  }
}

export function EmailTemplatesPanel({ initialKind }: { initialKind?: string | null }) {
  const intl = useIntl();
  const { data, loading, error, refetch } = useQuery(EmailTemplatesDocument);
  const [updateEmailTemplate] = useMutation(UpdateEmailTemplateDocument);
  const templates = data?.emailTemplates ?? [];
  const appName = data?.systemSettings?.appName?.trim() || "Smycles";
  // Without a settings row the built-in DE applies; a row with null holds an unsupported value (GENESIS-0079).
  const storedInstanceLanguage = data?.systemSettings
    ? data.systemSettings.communicationLanguage
    : CommunicationLanguage.De;
  const instanceLanguage = storedInstanceLanguage ?? CommunicationLanguage.De;
  // Kept per kind: a failed discard belongs to the combined text of that kind, not to the one shown next.
  const [discardFailure, setDiscardFailure] = useState<{ kind: EmailTemplateKind; message: string } | null>(null);
  const kinds = uniqueKinds(templates);
  const [view, setView] = useState<TemplateView>("editor");
  const [selectedKind, setSelectedKind] = useState<string | null>(initialKind ?? null);
  const [selectedLanguage, setSelectedLanguage] = useState<CommunicationLanguage | null>(null);
  const kind = kinds.find((candidate) => candidate === selectedKind) ?? kinds[0];
  const variants = templates.filter((template) => template.kind === kind);
  // A kind with language variants opens in the instance default, the language most recipients get.
  const selected =
    variants.find((template) => template.language === (selectedLanguage ?? instanceLanguage)) ?? variants[0];
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const draftOf = (template: Template): Draft =>
    drafts[draftKey(template)] ?? { subject: template.subject ?? null, body: template.body ?? null };
  // Anything but AVAILABLE is read-only: the store is disabled on this server (rollout default) or not
  // reachable, so a save could not succeed. The fields stay visible so the effective text is still readable.
  const isWritable = (template: Template) => template.storageStatus === EmailTemplateStorageStatus.Available;
  const isDirty = (template: Template) => {
    const draft = drafts[draftKey(template)];
    return Boolean(
      isWritable(template) &&
        draft &&
        (draft.subject !== (template.subject ?? null) || draft.body !== (template.body ?? null)),
    );
  };
  const activeDraft = selected ? draftOf(selected) : null;
  const draftSubject = activeDraft?.subject ?? null;
  const draftBody = activeDraft?.body ?? null;
  const writable = selected ? isWritable(selected) : false;
  // UNAVAILABLE: the stored text is unknown, so the fields stay empty instead of showing a default that may
  // be wrong. DISABLED keeps the registry default, because nothing else can be in effect on that server.
  const unavailable = selected?.storageStatus === EmailTemplateStorageStatus.Unavailable;
  const shownSubject = unavailable ? "" : (draftSubject ?? selected?.defaultSubject ?? "");
  const shownBody = unavailable ? "" : (draftBody ?? selected?.defaultBody ?? "");

  const forgetDrafts = (keys: readonly string[]) =>
    setDrafts((current) => {
      const next = { ...current };
      for (const key of keys) delete next[key];
      return next;
    });

  usePendingScope(`email-template:${kind ?? "none"}`, {
    dirty: variants.some(isDirty),
    save: async () => {
      const changed = variants.filter(isDirty).map((template) => {
        const draft = draftOf(template);
        return {
          template,
          subject: draft.subject?.trim() || null,
          body: draft.body?.trim() || null,
        };
      });
      // Every changed language is checked before the first one is written, so a save never stops half way.
      for (const { template, subject, body } of changed) {
        const invalid = validationMessage(intl, template, subject, body, appName);
        if (invalid) {
          throw new LocalizedError(invalid);
        }
      }
      try {
        for (const { template, subject, body } of changed) {
          await updateEmailTemplate({
            variables: {
              input: template.language
                ? { kind: template.kind, language: template.language, subject, body }
                : { kind: template.kind, subject, body },
            },
          });
        }
        await refetch();
        forgetDrafts(changed.map(({ template }) => draftKey(template)));
      } catch (cause) {
        throw new LocalizedError(userErrorMessage(intl, cause), { cause });
      }
    },
    discard: () => forgetDrafts(variants.map(draftKey)),
  });

  const setDraft = (template: Template, draft: Draft) =>
    setDrafts((current) => ({ ...current, [draftKey(template)]: draft }));

  const discardLegacy = async (template: Template) => {
    setDiscardFailure(null);
    try {
      // Without a language the kind's combined text is addressed; null texts discard it (GENESIS-0079).
      await updateEmailTemplate({ variables: { input: { kind: template.kind, subject: null, body: null } } });
      await refetch();
    } catch (cause) {
      setDiscardFailure({ kind: template.kind, message: userErrorMessage(intl, cause) });
    }
  };

  const kindLabel = (templateKind: EmailTemplateKind) =>
    intl.formatMessage({
      id: `adminSettings.emailTemplates.kind.${templateKind}`,
      defaultMessage: templateKind === EmailTemplateKind.Invitation ? "Invitation" : templateKind,
    });

  const localInvalid =
    selected && activeDraft
      ? validationMessage(intl, selected, draftSubject?.trim() || null, draftBody?.trim() || null, appName)
      : null;

  return (
    <div className="space-y-4">
      {error ? (
        <p className="smycles-field-meta text-danger" role="alert">
          {userErrorMessage(intl, error)}
        </p>
      ) : null}
      {data ? <InstanceMailLanguageCard value={storedInstanceLanguage} /> : null}
      <AdminMasterDetail
        title={intl.formatMessage({
          id: "adminSettings.emailTemplates.title",
          defaultMessage: "Email templates",
        })}
        count={kinds.length}
        list={
          loading && templates.length === 0 ? (
            <div className="smycles-field-meta text-ink-muted">
              {intl.formatMessage({ id: "adminSettings.emailTemplates.loading", defaultMessage: "Loading…" })}
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {kinds.map((templateKind) => (
                <MenuItem
                  id={`email-template-${templateKind}`}
                  key={templateKind}
                  active={templateKind === kind}
                  onClick={() => setSelectedKind(templateKind)}
                >
                  {kindLabel(templateKind)}
                </MenuItem>
              ))}
            </div>
          )
        }
      >
        {selected && activeDraft ? (
          <SettingsCard
            title={kindLabel(selected.kind)}
            sub={intl.formatMessage({
              id: "adminSettings.emailTemplates.sub",
              defaultMessage: "Subject and content used for the next system email.",
            })}
          >
            <div className="space-y-4">
              {!writable ? (
                <div className="smycles-hint text-smycles-meta text-ink" role="status">
                  {intl.formatMessage(
                    selected.storageStatus === EmailTemplateStorageStatus.Disabled
                      ? {
                          id: "adminSettings.emailTemplates.storageDisabled",
                          defaultMessage:
                            "Editing email templates is not enabled on this server. The template is shown read-only.",
                        }
                      : {
                          id: "adminSettings.emailTemplates.storageUnavailable",
                          defaultMessage:
                            "The template store is currently unreachable. The template is shown read-only and stored changes may not be visible.",
                        },
                  )}
                </div>
              ) : null}
              <div className="flex flex-wrap items-center justify-between gap-3">
                {selected.language ? (
                  <SegmentedControl
                    aria-label={intl.formatMessage({
                      id: "adminSettings.emailTemplates.languageLabel",
                      defaultMessage: "Template language",
                    })}
                    size="sm"
                    value={selected.language}
                    onChange={setSelectedLanguage}
                    options={COMMUNICATION_LANGUAGE_OPTIONS.filter((option) =>
                      variants.some((template) => template.language === option.value),
                    )}
                  />
                ) : (
                  <span />
                )}
                <SegmentedControl
                  aria-label={intl.formatMessage({
                    id: "adminSettings.emailTemplates.viewLabel",
                    defaultMessage: "Template view",
                  })}
                  size="sm"
                  value={view}
                  onChange={setView}
                  options={[
                    {
                      value: "editor",
                      label: intl.formatMessage({
                        id: "adminSettings.emailTemplates.viewEditor",
                        defaultMessage: "Editor",
                      }),
                    },
                    {
                      value: "preview",
                      label: intl.formatMessage({
                        id: "adminSettings.emailTemplates.viewPreview",
                        defaultMessage: "Preview",
                      }),
                    },
                  ]}
                />
              </div>

              {selected.language && (selected.legacySubject || selected.legacyBody) ? (
                <LegacyEmailTemplateNotice
                  subject={selected.legacySubject ?? null}
                  body={selected.legacyBody ?? null}
                  writable={writable}
                  failure={discardFailure?.kind === selected.kind ? discardFailure.message : null}
                  onDiscard={() => discardLegacy(selected)}
                />
              ) : null}

              {view === "editor" ? (
                <>
                  <label className="block">
                    <span className="smycles-field-label">
                      {intl.formatMessage({
                        id: "adminSettings.emailTemplates.subject",
                        defaultMessage: "Subject",
                      })}
                    </span>
                    <TextInput
                      value={shownSubject}
                      onChange={(event) => setDraft(selected, { ...activeDraft, subject: event.target.value })}
                      disabled={loading || !writable}
                      className="mt-1"
                    />
                  </label>

                  <label className="block">
                    <span className="smycles-field-label">
                      {intl.formatMessage({
                        id: "adminSettings.emailTemplates.body",
                        defaultMessage: "Content",
                      })}
                    </span>
                    <TextArea
                      mono
                      value={shownBody}
                      onChange={(event) => setDraft(selected, { ...activeDraft, body: event.target.value })}
                      disabled={loading || !writable}
                      rows={18}
                      className="mt-1"
                    />
                  </label>
                </>
              ) : selected.language ? (
                <EmailTemplateLanguagePreview
                  kind={selected.kind}
                  language={selected.language}
                  subject={draftSubject?.trim() || null}
                  body={draftBody?.trim() || null}
                  invalid={localInvalid}
                />
              ) : (
                <EmailTemplatePreview
                  subject={shownSubject}
                  body={shownBody}
                  appName={appName}
                  subjectLabel={intl.formatMessage({
                    id: "adminSettings.emailTemplates.subject",
                    defaultMessage: "Subject",
                  })}
                  previewLabel={intl.formatMessage({
                    id: "adminSettings.emailTemplates.previewLabel",
                    defaultMessage: "Email preview",
                  })}
                  approximationHint={intl.formatMessage({
                    id: "adminSettings.emailTemplates.previewApproximationHint",
                    defaultMessage:
                      "This preview is approximate. Keycloak and the recipient's email client determine the final appearance.",
                  })}
                  linkLabel={intl.formatMessage({
                    id: "adminSettings.emailTemplates.previewLink",
                    defaultMessage: "Individual invitation link",
                  })}
                  emptyBodyLabel={intl.formatMessage({
                    id: "adminSettings.emailTemplates.previewEmptyBody",
                    defaultMessage: "No content.",
                  })}
                  emptySubjectLabel={intl.formatMessage({
                    id: "adminSettings.emailTemplates.previewEmptySubject",
                    defaultMessage: "No subject.",
                  })}
                />
              )}

              <p className="smycles-field-meta leading-relaxed text-ink-muted">
                {intl.formatMessage(
                  {
                    id: "adminSettings.emailTemplates.placeholders",
                    defaultMessage: "Available placeholders: {tokens}",
                  },
                  {
                    tokens: selected.placeholders
                      .map((placeholder) =>
                        placeholder.required
                          ? intl.formatMessage(
                              {
                                id: "adminSettings.emailTemplates.placeholderRequired",
                                defaultMessage: "{token} (required)",
                              },
                              { token: placeholder.token },
                            )
                          : placeholder.token,
                      )
                      .join(", "),
                  },
                )}
              </p>
              <p className="smycles-field-meta leading-relaxed text-ink-muted">
                {intl.formatMessage({
                  id: selected.kind === EmailTemplateKind.Invitation ? "adminSettings.emailTemplates.realmWideHint"
                    : selected.kind === EmailTemplateKind.ListingApplicationConfirmation ? "adminSettings.emailTemplates.applicationConfirmationHint"
                    : selected.kind === EmailTemplateKind.ListingPowerQuiz ? "adminSettings.emailTemplates.powerQuizHint"
                    : selected.kind === EmailTemplateKind.ListingApplicationReceipt ? "adminSettings.emailTemplates.applicationReceiptHint"
                    : selected.kind === EmailTemplateKind.ListingApplicationReceivedNotice
                      || selected.kind === EmailTemplateKind.ListingMatchResultNotice ? "adminSettings.emailTemplates.listingNoticeHint"
                    : "adminSettings.emailTemplates.applicationMessageHint",
                  defaultMessage:
                    "This text applies to all Keycloak action emails in the realm, including credential resets initiated in the Admin Console.",
                })}
              </p>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="smycles-field-meta text-ink-muted">
                  {selected.language && selected.source
                    ? intl.formatMessage(
                        {
                          id: "adminSettings.emailTemplates.sourceLine",
                          defaultMessage: "Currently sent: {source}",
                        },
                        { source: sourceLabel(intl, selected.source) },
                      )
                    : writable && draftSubject === null && draftBody === null
                      ? intl.formatMessage({
                          id: "adminSettings.emailTemplates.defaultActive",
                          defaultMessage: "The default is currently active.",
                        })
                      : null}
                </div>
                {writable && (draftSubject !== null || draftBody !== null) ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDraft(selected, { subject: null, body: null })}
                  >
                    {intl.formatMessage({
                      id: "adminSettings.emailTemplates.reset",
                      defaultMessage: "Use default",
                    })}
                  </Button>
                ) : null}
              </div>
            </div>
          </SettingsCard>
        ) : (
          <SettingsCard
            title={intl.formatMessage({
              id: "adminSettings.emailTemplates.title",
              defaultMessage: "Email templates",
            })}
          >
            <div className="smycles-field-meta text-ink-muted">
              {intl.formatMessage({
                id: "adminSettings.emailTemplates.empty",
                defaultMessage: "No email templates are available.",
              })}
            </div>
          </SettingsCard>
        )}
      </AdminMasterDetail>
    </div>
  );
}
